import { useEffect, useState, type RefObject } from 'react';

import type { TocHeading } from '../model/docs.type';

/** 소수점 반올림 오차. 앵커로 이동한 제목은 도착선에서 이 안쪽에 멈춘다. */
const LANDING_TOLERANCE_PX = 2;
/**
 * 감시 구역 아래쪽 경계 — 화면 아래에서 잘라낼 비율. 위쪽 경계는 도착선(px)이다.
 * %로 두는 이유: 창 높이가 바뀌어도 감시 구역을 다시 만들 필요가 없다.
 * 구역이 너무 얇으면 빠른 스크롤에서 제목이 구역을 한 프레임에 건너뛰어 신호가 빠진다.
 */
const WATCH_BOTTOM_CUT = '-60%';

/**
 * 앵커(`#s-3`)로 이동했을 때 제목이 멈추는 높이 = scroll-padding(헤더) + scroll-margin(제목).
 * 숫자를 따로 적지 않고 CSS 에서 읽는다 — 목차 클릭으로 도착한 위치와 "현재 절"
 * 판정선이 항상 같아야 클릭한 항목이 그대로 강조된다.
 */
function landingLine(heading: HTMLElement): number {
  const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  const margin = parseFloat(getComputedStyle(heading).scrollMarginTop) || 0;
  return padding + margin + LANDING_TOLERANCE_PX;
}

/** 도착선을 지난 마지막 절. 끝까지 내려 더 못 올라오는 마지막 절들은 해시로 고른 절을 따른다. */
function pickActive(nodes: HTMLElement[]): string | undefined {
  if (!nodes.length) return undefined;

  const root = document.documentElement;
  const atBottom = Math.ceil(window.scrollY + window.innerHeight) >= root.scrollHeight;
  if (atBottom) {
    const hashed = nodes.find((node) => `#${node.id}` === decodeURIComponent(window.location.hash));
    const visible = hashed && hashed.getBoundingClientRect().top < window.innerHeight;
    return visible ? hashed.id : nodes[nodes.length - 1].id;
  }

  const line = landingLine(nodes[0]);
  let active = nodes[0].id;
  for (const node of nodes) {
    if (node.getBoundingClientRect().top > line) break;
    active = node.id;
  }
  return active;
}

/**
 * 본문에서 목차를 만든다. 매니페스트가 아니라 **렌더된 DOM** 에서 읽는 이유:
 * 번호·앵커 id 를 렌더러가 매기므로, 같은 값을 쓰려면 그려진 결과를 읽는 게 가장 확실하다.
 *
 * "지금 보는 절"은 window 이벤트 리스너 없이 IntersectionObserver 로 따라간다.
 * 감시 구역의 위쪽 경계를 앵커 도착선에 맞추고, 신호가 올 때마다 `pickActive` 로
 * 다시 고른다 — 신호는 "다시 계산할 때"라는 알람일 뿐, 판정은 도착선 기준 하나다.
 * 목차를 누른 경우는 `select` 로 바로 표시한다(페이지 끝의 마지막 절은 도착선까지
 * 못 올라와 감시 구역 신호만으로는 표시되지 않는다).
 *
 * `deps` 가 바뀌면(=문서가 바뀌면) 다시 읽는다.
 */
export function useTocHeadings(
  containerRef: RefObject<HTMLElement | null>,
  deps: readonly unknown[],
): {
  headings: readonly TocHeading[];
  activeId: string | undefined;
  /** 목차 클릭 — 스크롤 결과를 기다리지 않고 누른 절을 바로 표시한다. */
  select: (id: string) => void;
} {
  const [headings, setHeadings] = useState<readonly TocHeading[]>([]);
  const [activeId, setActiveId] = useState<string>();

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    // 목차는 h2(번호 붙은 절)만. h3 는 본문 안 소제목이라 목차를 길게 만들 뿐이다.
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('h2[id]'));
    setHeadings(
      nodes.map((node, index) => ({
        id: node.id,
        // 렌더러가 붙인 번호를 그대로 쓴다 — 본문과 목차 번호가 어긋나면 안 된다.
        number: Number(node.dataset.docsNumber) || index + 1,
        // 렌더러가 제목 본문만 `[data-docs-heading]` 으로 감싼다. 번호·앵커('#')가
        // 목차에 섞이지 않으면서, 코드·굵게로 시작하는 제목도 통째로 살아난다.
        text: (
          node.querySelector('[data-docs-heading]')?.textContent ??
          node.textContent ??
          ''
        ).trim(),
      })),
    );

    // `…/cbs#s-30` 으로 들어온 경우. 본문이 API 응답 뒤에 그려지므로 브라우저의
    // 기본 해시 이동은 제목이 생기기 전에 끝나 버린다 — 본문을 읽은 지금 다시 맞춘다.
    const target = window.location.hash
      ? document.getElementById(decodeURIComponent(window.location.hash.slice(1)))
      : null;
    target?.scrollIntoView();
    setActiveId(pickActive(nodes));
    if (!nodes.length) return;

    // 감시 구역: 위 = 앵커 도착선(CSS 에서 읽은 px), 아래 = 화면의 40% 지점.
    // 제목이 구역을 드나들 때마다 도착선 기준으로 다시 고른다.
    const observer = new IntersectionObserver(
      () => setActiveId(pickActive(nodes)),
      {
        rootMargin: `-${landingLine(nodes[0])}px 0px ${WATCH_BOTTOM_CUT} 0px`,
        threshold: 0,
      },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { headings, activeId, select: setActiveId };
}
